import { useQuery, useQueries, useMutation } from '@tanstack/react-query';
import { queryClient } from '@/shared/services/queryClient';
import {
  greenhousesApi,
  type CreateGreenhouseRequest,
  type GreenhouseLayoutParams,
  type UpdateGreenhouseRequest,
} from '../services/greenhousesApi';

export const useGreenhouses = (enabled = true) => {
  return useQuery({
    enabled,
    queryKey: ['greenhouses'],
    queryFn: greenhousesApi.getAllGreenhouses,
  });
};

export const useGreenhouseLayout = (params: GreenhouseLayoutParams) => {
  return useQuery({
    queryKey: ['greenhouses', 'layout', params],
    queryFn: () => greenhousesApi.getLayout(params),
    placeholderData: (previous) => previous,
  });
};

export const useAvailableBeds = (greenhouseId: string) => {
  return useQuery({
    queryKey: ['greenhouses', greenhouseId, 'beds-available'],
    queryFn: () => greenhousesApi.getAvailableBeds(greenhouseId),
    enabled: !!greenhouseId,
  });
};

/** All beds for a greenhouse (occupied or not). Used when the caller needs the full picture. */
export const useGreenhouseBeds = (greenhouseId: string) => {
  return useQuery({
    queryKey: ['greenhouses', greenhouseId, 'beds-all'],
    queryFn: () => greenhousesApi.getGreenhouseBeds(greenhouseId),
    enabled: !!greenhouseId,
  });
};

/** All beds across multiple greenhouses. Returns combined results from each. */
export const useMultiGreenhouseBeds = (greenhouseIds: string[]) => {
  return useQueries({
    queries: greenhouseIds.map((id) => ({
      queryKey: ['greenhouses', id, 'beds-all'],
      queryFn: () => greenhousesApi.getGreenhouseBeds(id),
      enabled: !!id,
    })),
  });
};

export const useGreenhouseById = (id: string) => {
  return useQuery({
    queryKey: ['greenhouses', id],
    queryFn: () => greenhousesApi.getGreenhouseById(id),
  });
};

export const useCreateGreenhouse = () => {
  return useMutation({
    mutationFn: (payload: CreateGreenhouseRequest) =>
      greenhousesApi.createGreenhouse(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['greenhouses'] });
    },
  });
};

export const useUpdateGreenhouse = (id: string) => {
  return useMutation({
    mutationFn: (payload: UpdateGreenhouseRequest) =>
      greenhousesApi.updateGreenhouse(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['greenhouses'] });
      queryClient.invalidateQueries({ queryKey: ['greenhouses', id] });
    },
  });
};

export const useDeleteGreenhouse = () => {
  return useMutation({
    mutationFn: (id: string) => greenhousesApi.deleteGreenhouse(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['greenhouses'] });
    },
  });
};
